// Helper functions for base64url to buffer and back
function bufferToBase64URLString(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function base64URLStringToBuffer(base64url: string): ArrayBuffer {
  const b64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (b64.length % 4)) % 4;
  const str = atob(b64.padEnd(b64.length + padLen, "="));
  const buffer = new ArrayBuffer(str.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < str.length; i++) {
    bytes[i] = str.charCodeAt(i);
  }
  return buffer;
}

export interface CrateIdentityOptions {
  /** The Publishable Key (e.g. pk_live_...) for your Organization */
  publishableKey: string;
  /** The domain of the Crate Identity Gateway */
  domain?: string;
}

export class CrateIdentity {
  private domain: string;
  private publishableKey: string;

  constructor(options: CrateIdentityOptions) {
    if (!options.publishableKey) {
      throw new Error("A publishableKey is required to initialize CrateIdentity.");
    }
    this.publishableKey = options.publishableKey;
    this.domain = options.domain || "https://api.crate.cc";
  }

  /**
   * Begins and completes a WebAuthn registration (Passkey creation)
   */
  async register(identifier: string): Promise<{ token: string }> {
    // 1. Begin Registration
    const beginRes = await fetch(`${this.domain}/identity/register/begin`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({ identifier }),
    });
    
    if (!beginRes.ok) {
      throw new Error(`Failed to begin registration: ${await beginRes.text()}`);
    }
    
    const resBody = await beginRes.json();
    const options = resBody.data.publicKey;

    // 2. Browser interaction
    let attResp;
    try {
        const publicKey = {
            ...options,
            challenge: base64URLStringToBuffer(options.challenge),
            user: {
                ...options.user,
                id: base64URLStringToBuffer(options.user.id),
            },
        };
        
        if (publicKey.excludeCredentials) {
            publicKey.excludeCredentials = (publicKey.excludeCredentials as unknown as Array<{ id: string }>).map((cred) => ({
                ...cred,
                id: base64URLStringToBuffer(cred.id),
            }));
        }

        const credential = await navigator.credentials.create({ publicKey }) as PublicKeyCredential;
        const response = credential.response as AuthenticatorAttestationResponse;

        attResp = {
            id: credential.id,
            rawId: bufferToBase64URLString(credential.rawId),
            type: credential.type,
            authenticatorAttachment: credential.authenticatorAttachment,
            response: {
                clientDataJSON: bufferToBase64URLString(response.clientDataJSON),
                attestationObject: bufferToBase64URLString(response.attestationObject),
            },
            clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {},
        };
    } catch (e) {
        throw new Error(`User cancelled or failed passkey creation: ${e}`);
    }

    // 3. Finish Registration
    const finishRes = await fetch(`${this.domain}/identity/register/finish`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({
        identifier,
        credential: attResp,
      }),
    });

    if (!finishRes.ok) {
      throw new Error(`Failed to finish registration: ${await finishRes.text()}`);
    }

    return await finishRes.json();
  }

  /**
   * Begins and completes a WebAuthn login (Passkey assertion)
   */
  async login(identifier: string): Promise<{ token: string }> {
    // 1. Begin Login
    const beginRes = await fetch(`${this.domain}/identity/login/begin`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({ identifier }),
    });
    
    if (!beginRes.ok) {
      throw new Error(`Failed to begin login: ${await beginRes.text()}`);
    }
    
    const resBody = await beginRes.json();
    const options = resBody.data.publicKey;

    // 2. Browser interaction
    let attResp;
    try {
        const publicKey = {
            ...options,
            challenge: base64URLStringToBuffer(options.challenge),
        };

        if (publicKey.allowCredentials) {
            publicKey.allowCredentials = (publicKey.allowCredentials as unknown as Array<{ id: string }>).map((cred) => ({
                ...cred,
                id: base64URLStringToBuffer(cred.id),
            }));
        }

        const credential = await navigator.credentials.get({ publicKey }) as PublicKeyCredential;
        const response = credential.response as AuthenticatorAssertionResponse;

        attResp = {
            id: credential.id,
            rawId: bufferToBase64URLString(credential.rawId),
            type: credential.type,
            authenticatorAttachment: credential.authenticatorAttachment,
            response: {
                clientDataJSON: bufferToBase64URLString(response.clientDataJSON),
                authenticatorData: bufferToBase64URLString(response.authenticatorData),
                signature: bufferToBase64URLString(response.signature),
                userHandle: response.userHandle ? bufferToBase64URLString(response.userHandle) : undefined,
            },
            clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {},
        };
    } catch (e) {
        throw new Error(`User cancelled or failed passkey assertion: ${e}`);
    }

    // 3. Finish Login
    const finishRes = await fetch(`${this.domain}/identity/login/finish`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({
        identifier,
        credential: attResp,
      }),
    });

    if (!finishRes.ok) {
      throw new Error(`Failed to finish login: ${await finishRes.text()}`);
    }

    return await finishRes.json();
  }

  /**
   * Registers a new identity using a password
   */
  async registerWithPassword(identifier: string, password: string): Promise<{ identity_id: string, token: string }> {
    const res = await fetch(`${this.domain}/identity/register/password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({ identifier, password }),
    });

    if (!res.ok) {
      throw new Error(`Failed to register with password: ${await res.text()}`);
    }

    const resBody = await res.json();
    return resBody.data;
  }

  /**
   * Log in using a password
   */
  async loginWithPassword(identifier: string, password: string): Promise<{ identity_id: string, token: string }> {
    const res = await fetch(`${this.domain}/identity/login/password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({ identifier, password }),
    });

    if (!res.ok) {
      throw new Error(`Failed to login with password: ${await res.text()}`);
    }

    const resBody = await res.json();
    return resBody.data;
  }

  /**
   * Log in using a magic link token
   */
  async loginWithMagicLink(token: string): Promise<{ identity_id: string, token: string }> {
    const res = await fetch(`${this.domain}/identity/login/magic`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({ token }),
    });

    if (!res.ok) {
      throw new Error(`Failed to login with magic link: ${await res.text()}`);
    }

    const resBody = await res.json();
    return resBody.data;
  }

  /**
   * Resets an identity's password
   */
  async resetPassword(identityId: string, newPassword: string): Promise<void> {
    const res = await fetch(`${this.domain}/identity/reset/password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({ identity_id: identityId, new_password: newPassword }),
    });

    if (!res.ok) {
      throw new Error(`Failed to reset password: ${await res.text()}`);
    }
  }

  /**
   * Adds a new passkey credential to an identity
   */
  async addPasskey(identityId: string): Promise<void> {
    // 1. Begin adding passkey
    const beginRes = await fetch(`${this.domain}/identity/credentials/add/begin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({ identity_id: identityId }),
    });

    if (!beginRes.ok) {
      throw new Error(`Failed to begin add passkey: ${await beginRes.text()}`);
    }

    const resBody = await beginRes.json();
    const options = resBody.data.publicKey;

    // Decode challenge and user.id
    options.challenge = base64URLStringToBuffer(options.challenge);
    options.user.id = base64URLStringToBuffer(options.user.id);
    if (options.excludeCredentials) {
      options.excludeCredentials = options.excludeCredentials.map((c: { id: string; type: string; transports?: string[] }) => ({
        ...c,
        id: base64URLStringToBuffer(c.id),
      }));
    }

    // 2. Invoke WebAuthn
    const credential = await navigator.credentials.create({
      publicKey: options,
    }) as PublicKeyCredential;

    if (!credential) {
      throw new Error("Failed to create credential");
    }

    // 3. Finish adding passkey
    const finishRes = await fetch(`${this.domain}/identity/credentials/add/finish`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({
        identity_id: identityId,
        credential: {
          id: credential.id,
          rawId: bufferToBase64URLString(credential.rawId),
          type: credential.type,
          response: {
            attestationObject: bufferToBase64URLString((credential.response as AuthenticatorAttestationResponse).attestationObject),
            clientDataJSON: bufferToBase64URLString(credential.response.clientDataJSON),
            transports: credential.response.getTransports ? credential.response.getTransports() : [],
          },
        },
      }),
    });

    if (!finishRes.ok) {
      throw new Error(`Failed to finish add passkey: ${await finishRes.text()}`);
    }
  }

  /**
   * Removes a passkey credential from an identity
   */
  async removePasskey(identityId: string, credentialId: string): Promise<void> {
    const res = await fetch(`${this.domain}/identity/credentials/remove`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      },
      body: JSON.stringify({ identity_id: identityId, credential_id: credentialId }),
    });

    if (!res.ok) {
      throw new Error(`Failed to remove passkey: ${await res.text()}`);
    }
  }

  /**
   * Lists all credentials for an identity
   */
  async listCredentials(identityId: string): Promise<{ type: string, credential_id: string, device_type?: string, created_at: string, last_used_at?: string }[]> {
    const res = await fetch(`${this.domain}/identity/credentials?identity_id=${encodeURIComponent(identityId)}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${this.publishableKey}`
      }
    });

    if (!res.ok) {
      throw new Error(`Failed to list credentials: ${await res.text()}`);
    }

    const resBody = await res.json();
    return resBody.data;
  }
}
